import React from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminAuthProvider } from "./auth/AdminAuthContext";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import AdminLayout from "./layout/AdminLayout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import QuotationsPage from "./pages/QuotationsPage";
import QuotationEditorPage from "./pages/QuotationEditorPage";
import QuotationPrintPage from "./pages/QuotationPrintPage";
import CustomQuotationsPage from "./pages/CustomQuotationsPage";
import CustomQuotationEditorPage from "./pages/CustomQuotationEditorPage";
import CustomQuestionsPrintPage from "./pages/CustomQuestionsPrintPage";
import ReportsPage from "./pages/ReportsPage";
import ResourcePage from "./pages/ResourcePage";
import NotFoundPage from "./pages/NotFoundPage";

const adminTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#8a5a3c",
      dark: "#5f3c26",
      light: "#b98a67",
      contrastText: "#fffaf5"
    },
    secondary: {
      main: "#c08b57",
      dark: "#9d6f43",
      light: "#dfbf98"
    },
    text: {
      primary: "#2f241b",
      secondary: "#766454"
    },
    background: {
      default: "#f5ede3",
      paper: "#fffaf4"
    }
  },
  typography: {
    fontFamily: "'Manrope', 'Plus Jakarta Sans', 'Segoe UI', sans-serif",
    fontSize: 13,
    h3: {
      fontFamily: "'Manrope', 'Plus Jakarta Sans', 'Segoe UI', sans-serif",
      fontSize: "2rem",
      lineHeight: 1.12,
      fontWeight: 800
    },
    h4: {
      fontFamily: "'Manrope', 'Plus Jakarta Sans', 'Segoe UI', sans-serif",
      fontSize: "1.55rem",
      lineHeight: 1.18,
      fontWeight: 800
    },
    h5: {
      fontFamily: "'Manrope', 'Plus Jakarta Sans', 'Segoe UI', sans-serif",
      fontSize: "1.2rem",
      lineHeight: 1.2,
      fontWeight: 700
    },
    h6: {
      fontFamily: "'Manrope', 'Plus Jakarta Sans', 'Segoe UI', sans-serif",
      fontSize: "0.98rem",
      lineHeight: 1.25,
      fontWeight: 700
    },
    body1: {
      fontSize: "0.94rem",
      lineHeight: 1.6
    },
    body2: {
      fontSize: "0.84rem",
      lineHeight: 1.55
    },
    caption: {
      fontSize: "0.72rem"
    },
    overline: {
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.18em"
    }
  },
  shape: {
    borderRadius: 4
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
            "radial-gradient(circle at top left, rgba(192, 139, 87, 0.08), transparent 24%), linear-gradient(180deg, #f8f2ea 0%, #f2e6d8 100%)"
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 22px 50px rgba(96, 68, 41, 0.08)"
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 14,
          fontWeight: 700,
          fontSize: "0.84rem",
          paddingInline: 16,
          paddingBlock: 10
        },
        contained: {
          boxShadow: "0 14px 28px rgba(114, 76, 45, 0.16)"
        },
        outlined: {
          borderColor: "rgba(111, 82, 58, 0.2)"
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 10
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: "small"
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: "rgba(255, 250, 244, 0.92)"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: "0.76rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#6f5947",
          borderBottom: "1px solid rgba(111, 82, 58, 0.12)"
        },
        body: {
          fontSize: "0.84rem",
          borderBottom: "1px solid rgba(111, 82, 58, 0.08)"
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(171, 131, 91, 0.05)"
          }
        }
      }
    }
  }
});

export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <ThemeProvider theme={adminTheme}>
        <CssBaseline />
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route element={<ProtectedAdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="quotations" element={<QuotationsPage />} />
              <Route path="quotations/new" element={<QuotationEditorPage />} />
              <Route path="quotations/:quotationId/edit" element={<QuotationEditorPage />} />
              <Route path="quotations/:quotationId/print" element={<QuotationPrintPage />} />
              <Route path="custom-quotations" element={<CustomQuotationsPage />} />
              <Route path="custom-quotations/new" element={<CustomQuotationEditorPage />} />
              <Route path="custom-quotations/:customQuotationId/edit" element={<CustomQuotationEditorPage />} />
              <Route path="custom-quotations/:customQuotationId/print" element={<CustomQuotationEditorPage printOnly />} />
              <Route path="customers" element={<ResourcePage resourceKey="customers" />} />
              <Route path="events" element={<ResourcePage resourceKey="events" />} />
              <Route path="templates" element={<ResourcePage resourceKey="templates" />} />
              <Route path="catalog" element={<ResourcePage resourceKey="catalog" />} />
              <Route path="categories" element={<ResourcePage resourceKey="categories" />} />
              <Route path="custom-questions" element={<ResourcePage resourceKey="custom-questions" />} />
              <Route path="custom-questions/print" element={<CustomQuestionsPrintPage />} />
              <Route path="users" element={<ResourcePage resourceKey="users" />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path=":resourceKey" element={<ResourcePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
      </ThemeProvider>
    </AdminAuthProvider>
  );
}
